import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom';
import './HeaderNav.css';
import { HeaderNav } from '.';
import { Button } from '../Buttons';
import { Segmented } from 'antd';
import { Control, Controller } from 'react-hook-form';
import gsap from 'gsap';

interface TabsProps {
  /** Array of tab labels or tab objects with optional icon and value */
  tabs: Array<string | { label: string; icon?: React.ReactNode; value?: string }>;

  /** References to the tab elements, used for positioning */
  tabRefs: any;

  /** Type of tab, determines rendering style */
  /** @default "Link" */
  type?: "Link" | "Button" | "Segmented";

  /** Size of the tab text */
  /** @default "headingxs" */
  size?: "headingxs" | "headinglg" | "textxs";

  /** Whether tab indicator has animation */
  /** @default false */
  animated?: boolean;

  /** The currently active tab */
  activeTab?: string;

  /** Callback function when tab changes */
  onTabChange?: (tab: string) => void;
}

function Tabs({ tabs, tabRefs, type = "Link", size = "headingxs", animated = false, activeTab, onTabChange }: TabsProps) {
  const indicatorRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const activeIndex = tabs.findIndex((tab) =>
      typeof tab === "object"
        ? (activeTab ?? "").startsWith(tab.value ?? "")
        : (activeTab ?? "").startsWith(tab.toLowerCase())
    );

    if (activeIndex !== -1 && tabRefs.current[activeIndex]) {
      const activeElement = tabRefs.current[activeIndex];

      const indicatorClassName = indicatorRef.current?.className;

      gsap.to(indicatorRef.current, {
        x: activeElement.offsetLeft,
        width: activeElement.offsetWidth,
        opacity: 1, // Ensure the indicator is visible
        duration: animated ? 0.1 : 0,
        ease: "power1.out",
        borderRadius: indicatorClassName?.includes("tabs-2")
          ? activeIndex === 0
            ? "30px 0 0 30px"
            : activeIndex === tabs.length - 1
              ? "0 30px 30px 0"
              : ""
          : "30px",
      });
    } else {
      gsap.to(indicatorRef.current, {
        opacity: 0, // Hide the indicator instead of setting width to 0
        duration: animated ? 0.1 : 0,
        ease: "power1.out",
      });
    }
  }, [activeTab, tabs]);

  switch (type) {
    case "Button":
      return (
        <ul>
          {tabs.map((tab, index) => {
            const isObject = typeof tab === "object";
            const label = isObject ? tab.label : tab;
            const icon = isObject ? tab.icon : undefined;
            const value = isObject ? tab.value : `/${label.toLowerCase()}`;

            return (
              <li key={index} ref={(el) => (tabRefs.current[index] = el)}>
                <Button
                  icon={icon}
                  className={activeTab === value ? "activeTab" : ""}
                  onClick={() => onTabChange && onTabChange(value || '')}
                >
                  <HeaderNav as="h1" size={size}>{label}</HeaderNav>
                </Button>
              </li>
            );
          })}
        </ul>
      );
    default:
    case "Link":
    case "Segmented":
      return (
        <ul className="flex relative">
          {tabs.map((tab, index) => {
            const isObject = typeof tab === "object";
            const label = isObject ? tab.label : tab;
            const value = isObject ? tab.value || "" : label;
            const icon = isObject ? tab.icon : undefined;

            // console.log({activeTab, startsWith: activeTab?.startsWith(`${value.toLowerCase()}/`), endsWith: activeTab?.endsWith(`${value.toLowerCase()}`)} )
            return (
              <li key={index} ref={(el) => (tabRefs.current[index] = el)} className="relative">
                <Link
                  to={value.toLowerCase()}
                  className={`px-4 py-2 relative z-10
                    ${activeTab === value.toLowerCase() || activeTab?.endsWith(`${value.toLowerCase()}`) || activeTab?.startsWith(`${value.toLowerCase()}/`)
                      ? "text-[#1F7A50] font-bold"
                      : "text-[#717680]"
                    }`}
                  onClick={() => onTabChange && onTabChange(value.toLowerCase())}
                >
                  {icon && <span className={`${label ? "pr-2" : ""}`}>{icon}</span>}
                  {label && <HeaderNav as="h1" size={size}>{label}</HeaderNav>}
                </Link>
              </li>
            );
          })}
          <div
            ref={indicatorRef}
            className={`absolute top-0 left-0 activeTab ${type === "Segmented" ? "tabs-2" : ""} transition-all`}
            style={{ width: "0px", height: "100%", position: "absolute", zIndex: 0 }}
          />
        </ul>
      );
  }
}

interface TabSliderProps {
  /**
   * An array of objects containing the value, label, and content of each tab.
   * Each tab object should have the following properties:
   * - `value`: the value of the tab
   * - `label`: the label of the tab
   * - `message`: an optional message to be displayed in the tab
   * - `component`: the content of the tab
   * - `footerContent`: an optional footer content
   * - `footerLink`: an optional footer link
   * - `footerLabel`: an optional footer label
   * @default `[]`
   */
  tabs: Array<{
    value: string,
    label: string,
    message?: string,
    component: React.ReactNode,
    footerContent?: string,
    footerLink?: React.ReactNode | string,
    footerLabel?: string
  }>;
  /**
   * The value of the currently active tab.
   * @default `''`
   */
  activeTab: string;
  /**
   * A function to set the active tab.
   * @default `() => {}`
   */
  setActiveTab: (tab: React.ReactNode | string) => void;
  /**
   * An optional title to be displayed above the tabs.
   * @default `undefined`
   */
  title?: string;
  className?: string;
  size?: "sm" | "md" | "lg";

}

function TabSlider({ tabs = [], activeTab, setActiveTab, title, className = 'w-full max-w-md mx-auto mt-[0.25rem]', size = 'md' }: TabSliderProps) {
   const [height, setHeight] = useState("auto")
  const contentRef = useRef<HTMLDivElement | null>(null)

  // Size-based styles
  const sizeStyles = {
    sm: {
      tab: "py-1.5 sm:py-2 text-xs sm:text-sm",
      header: "p-0.5",
    },
    md: {
      tab: "py-1.5 sm:py-2 text-xs sm:text-sm",
      header: "p-0.5 sm:p-1",
    },
    lg: {
      tab: "py-2 sm:py-3 text-sm sm:text-base",
      header: "p-1 sm:p-1.5",
    },
  }[size]

  useEffect(() => {
    const updateHeight = () => {
      if (contentRef.current) {
        setHeight(`${contentRef.current.scrollHeight + 10}px`)
      }
    }

    updateHeight()

    const observer = new MutationObserver(updateHeight)
    if (contentRef.current) {
      observer.observe(contentRef.current, { childList: true, subtree: true })
    }

    return () => observer.disconnect()
  }, [activeTab])

  return (
    <>
      {title && <p className="text-xs sm:text-sm font-normal text-gray-700">{title}</p>}
      {tabs?.find((tab) => tab.value === activeTab)?.message && (
        <p className="text-[#535862] mb-3 sm:mb-5 text-center text-xs sm:text-sm">
          {tabs?.find((tab) => tab.value === activeTab)?.message}
        </p>
      )}
      <div className={className}>
        {/* Tabs Header */}
        <div
          className={`relative flex bg-gray-200 rounded-full items-center justify-center ${sizeStyles.header}`}
        >
          {tabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={`flex-1 text-center text-nowrap rounded-full font-semibold z-10 transition-colors ${sizeStyles.tab} ${
                activeTab === tab.value ? "text-[#414651]" : "text-gray-600"
              }`}
            >
              {tab.label}
            </button>
          ))}
          {/* Sliding Pill */}
          <div
            className="absolute top-0 left-0 h-full bg-white border border-[#D5D7DA] rounded-full transition-transform duration-300"
            style={{
              width: `${100 / tabs.length}%`,
              transform: `translateX(${
                tabs.findIndex((tab) => tab.value === activeTab) * 100
              }%)`,
            }}
          ></div>
        </div>

        {/* Content Container with Height Animation */}
        {tabs.find((tab) => tab.value === activeTab)?.component && (
          <div
            className="relative overflow-hidden transition-all duration-300"
            style={{ height }}
          >
            <div ref={contentRef}>
              {tabs.find((tab) => tab.value === activeTab)?.component}
              <p className="text-xs sm:text-sm text-center font-light text-gray-500 dark:text-gray-400">
                {tabs.find((tab) => tab.value === activeTab)?.footerContent}
                <span
                  onClick={() =>
                    setActiveTab(
                      tabs.find((tab) => tab.value === activeTab)?.footerLink
                    )
                  }
                  className="font-medium text-primary-600 cursor-pointer"
                >
                  {" "}
                  {
                    tabs.find((tab) => tab.value === activeTab)?.footerLabel
                  }
                </span>
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

interface CustomSegmentedProps {
  label?: string;
  onChange?: (value: any) => void;
  value?: any;
  name?: string;
  options: any;
  control?: Control<any>;
  rest?: any;
  defaultValue?: any;
  variant?: string; // allow multiple space-separated variants
}

const CustomSegmented = ({
  label,
  onChange,
  value,
  name = '',
  options,
  control,
  defaultValue,
  variant = 'rounded',
  ...rest
}: CustomSegmentedProps) => {
  const segmentedRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const scrollToSelected = () => {
      setTimeout(() => {
        const selectedOption =
          segmentedRef.current?.querySelector('.ant-segmented-item-selected');
        selectedOption?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }, 50);
    };
    scrollToSelected();
  }, [value]);

  return (
    <div>
      {label && <p className="text-sm font-medium text-gray-700 pb-1">{label}</p>}
      <div
        ref={segmentedRef}
        className={`overflow-x-auto ${variant
          .split(' ')
          .map(v => `custom-segmented-${v}`)
          .join(' ')}`}
      >
        <Controller
          name={name}
          control={control}
          render={() => (
            <Segmented
              options={options}
              onChange={onChange}
              value={value}
              defaultValue={defaultValue}
              {...rest}
            />
          )}
        />
      </div>
    </div>
  );
};



export { Tabs, TabSlider, CustomSegmented }